import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { FiLock, FiUser, FiAlertCircle } from "react-icons/fi";
import { SiDiscord } from "react-icons/si";
import "./Login.css";

// Full URL needed for page navigations (Discord OAuth redirect)
const SERVER_URL = window.location.hostname === "localhost"
  ? "http://localhost:5000"
  : "";

const Login = ({ setUser }) => {
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const oauthError = searchParams.get("error");
    if (oauthError === "oauth_failed") setError("Discord login failed. Please try again.");
    else if (oauthError === "invalid_code") setError("Invalid OAuth code. Please try again.");
  }, [searchParams]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post("/api/auth/login", form);
      if (res.data.success) {
        const user = { username: res.data.username, token: res.data.token, role: res.data.role };
        setUser(user);
        localStorage.setItem("zft_user", JSON.stringify({ user, loginTime: Date.now() }));
        navigate("/");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscordLogin = () => {
    window.location.href = `${SERVER_URL}/auth/discord`;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          {/* Header */}
          <div className="login-header">
            <h1 className="login-title font-display">Sign In</h1>
            <p className="login-subtitle">
              Log in as an admin or continue with Discord to register for tournaments
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-alert">
              <FiAlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Discord Login */}
          <button onClick={handleDiscordLogin} className="btn-discord" type="button">
            <SiDiscord size={22} />
            <span>Continue with Discord</span>
          </button>

          <div className="login-divider">
            <span>or admin login</span>
          </div>

          {/* Admin Form */}
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                <FiUser size={16} />
                <span>Username</span>
              </label>
              <input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                disabled={loading}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <FiLock size={16} />
                <span>Password</span>
              </label>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                disabled={loading}
                className="form-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-block"
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: 20, height: 20 }} />
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Admin Sign In</span>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <Link to="/" className="back-link">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
